import { useId, type ReactNode } from 'react';

/**
 * Configurações (ADR 0128): blocos com título e, dentro, uma linha por preferência: o nome e a
 * explicação à esquerda, o controle à direita (`Switch`, `Select`, um botão).
 */
export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="ui-panel flex flex-col rounded-lg bg-sheet">
      <header className="flex flex-col gap-0.5 border-b border-rule px-5 py-3">
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="text-sm text-muted">{description}</p>}
      </header>
      <div className="flex flex-col divide-y divide-rule">{children}</div>
    </section>
  );
}

export function SettingRow({
  label,
  description,
  control,
}: {
  label: ReactNode;
  description?: ReactNode;
  /** O controle; recebe o id do rótulo em `aria-labelledby` por `labelId`. */
  control: (labelId: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span id={id} className="text-sm font-medium">
          {label}
        </span>
        {description && <span className="text-xs text-muted">{description}</span>}
      </div>
      <div className="shrink-0">{control(id)}</div>
    </div>
  );
}

/** Chave de ligar e desligar, com o estado lido por leitores de tela (`role="switch"`). */
export function Switch({
  checked,
  onChange,
  labelledBy,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  labelledBy: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition focus-visible:ring-3 focus-visible:ring-accent/30 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-accent' : 'bg-control-border'
      }`}
    >
      <span
        aria-hidden
        className={`inline-block size-5 rounded-full bg-sheet shadow-sm transition ${
          checked ? 'translate-x-5.5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

/** Escolha de uma opção entre poucas, lado a lado (para o controle de uma `SettingRow`). */
export function SegmentedChoice<V extends string>({
  values,
  value,
  onChange,
  labelOf,
  labelledBy,
  disabled,
}: {
  values: readonly V[];
  value: V;
  onChange: (v: V) => void;
  labelOf: (v: V) => string;
  labelledBy: string;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      className="inline-flex flex-wrap gap-1 rounded-lg border border-rule bg-paper p-1"
    >
      {values.map((v) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={v === value}
          disabled={disabled}
          onClick={() => onChange(v)}
          className={`rounded-md px-3 py-1 text-sm disabled:cursor-not-allowed ${
            v === value ? 'ui-panel bg-sheet font-medium text-ink' : 'text-muted hover:text-ink'
          }`}
        >
          {labelOf(v)}
        </button>
      ))}
    </div>
  );
}
